import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, TrendingUp, TrendingDown } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { getCategoryComparison } from '../services/api';
import DataBadge from './DataBadge';
import UZBEK_NAMES from '../constants/uzbekNames';

function fmt(n) {
  if (!n && n !== 0) return '—';
  const num = Number(n);
  if (num >= 1e9) return (num / 1e9).toFixed(1) + 'B';
  if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M';
  if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K';
  return String(num);
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('uz-UZ', { year: 'numeric', month: 'short', day: 'numeric' });
}

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(6,18,13,0.95)', border: '1px solid rgba(16,185,129,0.2)',
      borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#f0fdf6',
      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
    }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, display: 'inline-block' }} />
          <span style={{ color: '#8fae9c' }}>{p.name}:</span>
          <span style={{ color: p.color, fontWeight: 700 }}>{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export default function HistoryModal({ catId, catName, catColor, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const displayName = UZBEK_NAMES[catName] || catName;
  const color = catColor || '#50C878';

  useEffect(() => {
    getCategoryComparison(catId)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setData({ hasData: false, daysCovered: 0 }); setLoading(false); });
  }, [catId]);

  // Close on backdrop click
  function handleBackdrop(e) {
    if (e.target === e.currentTarget) onClose();
  }

  // Close on Escape
  useEffect(() => {
    const fn = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose]);

  const canCompare = data?.hasData && data.daysCovered >= 2;

  // Build normalised % change series for the chart (handles dual-scale problem)
  const chartData = canCompare
    ? data.fullHistory.map(r => {
        const first = data.firstSnapshot;
        const subBase = Number(first.subscriber_count) || 1;
        const viewBase = Number(first.view_count) || 1;
        return {
          date: new Date(r.snapshot_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          'Obunachilar': Number(r.subscriber_count),
          "Ko'rishlar": Number(r.view_count),
          subPct: parseFloat(((Number(r.subscriber_count) - subBase) / subBase * 100).toFixed(2)),
          viewPct: parseFloat(((Number(r.view_count) - viewBase) / viewBase * 100).toFixed(2)),
        };
      })
    : [];

  // Use % change if the two metrics are wildly different in scale
  const usePercent = canCompare && (
    Number(data.lastSnapshot.view_count) > Number(data.lastSnapshot.subscriber_count) * 10
  );

  return (
    <AnimatePresence>
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={handleBackdrop}
        style={{
          position: 'fixed', inset: 0, zIndex: 500,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '24px 16px',
        }}
      >
        <motion.div
          key="modal"
          initial={{ opacity: 0, scale: 0.92, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 16 }}
          transition={{ type: 'spring', stiffness: 340, damping: 28 }}
          style={{
            width: '100%', maxWidth: 620,
            background: 'var(--surface, #0f231a)',
            border: '1px solid var(--border, #1f3d2d)',
            borderRadius: 20,
            boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
            overflow: 'hidden',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '18px 24px',
            borderBottom: '1px solid var(--border, #1f3d2d)',
            background: `linear-gradient(135deg, ${color}18, transparent)`,
          }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>Tarixiy ma'lumot</div>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>
                {displayName}
              </h2>
            </div>
            <button
              onClick={onClose}
              style={{
                width: 32, height: 32, borderRadius: '50%', border: 'none',
                background: 'rgba(148,163,184,0.12)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--text-secondary)',
              }}
            >
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: '24px', maxHeight: '75vh', overflowY: 'auto' }}>

            {loading && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[80, 160, 40].map((h, i) => (
                  <div key={i} className="skeleton" style={{ height: h, borderRadius: 12 }} />
                ))}
              </div>
            )}

            {!loading && !canCompare && (
              <div style={{
                textAlign: 'center', padding: '32px 16px',
                color: 'var(--text-secondary)', lineHeight: 1.7,
              }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
                <p style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>
                  Hali yetarli tarixiy ma'lumot yo'q
                </p>
                <p style={{ fontSize: 13 }}>
                  Ma'lumotlar har kuni avtomatik yig'ilmoqda —{' '}
                  <strong style={{ color }}>{data?.daysCovered || 0} kunlik</strong> ma'lumot mavjud.
                  To'liq taqqoslash uchun biroz kuting.
                </p>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  marginTop: 16, padding: '8px 16px', borderRadius: 99,
                  background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.25)',
                  color: '#eab308', fontSize: 12, fontWeight: 600,
                }}>
                  Kamida 2 kunlik ma'lumot kerak
                </div>
              </div>
            )}

            {!loading && canCompare && (
              <>
                {/* Before / After comparison cards */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, alignItems: 'center', marginBottom: 24 }}>
                  {/* First snapshot */}
                  <div style={{
                    background: 'var(--bg-secondary)', borderRadius: 14,
                    padding: '16px', border: '1px solid var(--border)',
                  }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Boshlang'ich
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                      {fmtDate(data.firstSnapshot.snapshot_date)}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 4 }}>
                      {fmt(data.firstSnapshot.subscriber_count)}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>obunachi</div>
                    {Number(data.firstSnapshot.view_count) > 0 && (
                      <>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)', marginTop: 8 }}>
                          {fmt(data.firstSnapshot.view_count)}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>ko'rish</div>
                      </>
                    )}
                  </div>

                  {/* Arrow + % change */}
                  <div style={{ textAlign: 'center', flexShrink: 0 }}>
                    <div style={{
                      fontSize: 11, fontWeight: 700,
                      color: data.subChangePercent >= 0 ? '#50C878' : '#f43f5e',
                      marginBottom: 4,
                    }}>
                      {data.subChangePercent >= 0 ? '+' : ''}{data.subChangePercent}%
                    </div>
                    {data.subChangePercent >= 0
                      ? <TrendingUp size={22} color="#50C878" />
                      : <TrendingDown size={22} color="#f43f5e" />}
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
                      {data.daysCovered} kun
                    </div>
                  </div>

                  {/* Last snapshot */}
                  <div style={{
                    background: `${color}12`,
                    borderRadius: 14, padding: '16px',
                    border: `1px solid ${color}40`,
                  }}>
                    <div style={{ fontSize: 11, color, marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Hozir
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                      {fmtDate(data.lastSnapshot.snapshot_date)}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 4 }}>
                      {fmt(data.lastSnapshot.subscriber_count)}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>obunachi</div>
                    {Number(data.lastSnapshot.view_count) > 0 && (
                      <>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)', marginTop: 8 }}>
                          {fmt(data.lastSnapshot.view_count)}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>ko'rish</div>
                      </>
                    )}
                  </div>
                </div>

                {/* Line chart */}
                <div style={{
                  background: 'var(--bg-secondary)', borderRadius: 14,
                  padding: '16px', border: '1px solid var(--border)', marginBottom: 14,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12 }}>
                    {usePercent ? "Foizli o'sish dinamikasi" : "Absolut qiymatlar dinamikasi"}
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(143,174,156,0.1)" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#5f7a6a' }} axisLine={false} tickLine={false} />
                      <YAxis
                        tickFormatter={v => usePercent ? `${v}%` : fmt(v)}
                        tick={{ fontSize: 10, fill: '#5f7a6a' }}
                        width={usePercent ? 40 : 52}
                        axisLine={false} tickLine={false}
                      />
                      <RTooltip content={usePercent ? undefined : <ChartTooltip />}
                        formatter={usePercent ? (v, name) => [`${v}%`, name] : undefined}
                        contentStyle={usePercent ? {
                          background: 'rgba(6,18,13,0.95)', border: '1px solid rgba(80,200,120,0.2)',
                          borderRadius: 10, color: '#f0fdf6', fontSize: 12,
                        } : undefined}
                      />
                      <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                      <Line
                        type="monotone"
                        dataKey={usePercent ? 'subPct' : 'Obunachilar'}
                        name="Obunachilar"
                        stroke={color}
                        strokeWidth={2.5}
                        dot={{ r: 3, fill: color }}
                        activeDot={{ r: 5 }}
                        isAnimationActive
                        animationDuration={800}
                      />
                      <Line
                        type="monotone"
                        dataKey={usePercent ? 'viewPct' : "Ko'rishlar"}
                        name="Ko'rishlar"
                        stroke="#0B3D2E"
                        strokeWidth={2.5}
                        dot={{ r: 3, fill: '#0B3D2E' }}
                        activeDot={{ r: 5 }}
                        strokeDasharray="5 3"
                        isAnimationActive
                        animationDuration={800}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'right' }}>
                  {data.daysCovered} kunlik real ma'lumot asosida <DataBadge isReal={true} label="Live" />
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
